import {Component, ChangeDetectorRef, Input} from "@angular/core";
import {ClientConfiguration} from "../services/configuration/client.config";
import {SessionService} from "../services/configuration/session.service";
import {AssignmentService, SelectionsAndCategories} from "../services/assignment/assignment.service";
import {EventBus} from "../event/EventBus";
import {PleaseWaitEvent, PleaseWaitTarget} from "../event/ContentEvents";
import "rxjs/add/operator/map";
import "rxjs/add/operator/subscribeOn";
import {ILibrarySelection} from "./library.objects";
import {ContentProgression, ErrorType} from "../constants";
import {RecognizerService} from "../services/recognition/recognizer.service";
import {ComponentBase} from "../base/ComponentBase";
import {CompletionState, RecordingBadge} from "../services/usermodel/usermodel.objects";
import {PipelineService} from "../services/pipeline/pipeline.service";
import {NavigationEvent} from "../event/NavigationEvent";
import {StudentConfiguration} from "../services/assignment/student.configuration";
import {ModalConfig} from "../services/notification/modal.config";
import {EnableExitButtonEvent} from "../event/ExitButtonEvents";
import {LibraryService} from "../services/library/library.service";
import {NetworkService} from "../services/interaction/network.service";
import {AudioQueueEvent, InterruptAudio} from "../event/AudioQueueEvent";
import {AudioQueueItem, TranslateAudio} from "../services/audio-playback/audioqueue.item";
import {AudioQueueService} from "../services/audio-playback/audioqueue.service";
import {BugsnagLoggingService} from "../services/logging/bugsnag-logging.service";
import {GAEventService} from "../services/analytics/GAEventServce";
import {BrowserAgent} from "../services/browser/browser.agent";
import {GESTURE_STATUS, GestureEvent} from "../event/GestureEvent";
import {debounceTime, filter, map} from "rxjs/operators";
import {PIPELINE_STATUS, PipelineEvent} from "../event/PipelineEvent";
import { BehaviorSubject, combineLatest } from "rxjs";

type Category = {label: string, icon: string, contentAreas?: string[], genres?: string[], numOfItems?: number};

@Component({
    selector: "library",
    templateUrl: "./library.component.html",
    styleUrls: ["./library.component.scss"]
})
export class LibraryComponent extends ComponentBase {

    public _selections: BehaviorSubject<ILibrarySelection[]> = new BehaviorSubject([]);
    get selections() {return this._selections.value}
    set selections(s) {this._selections.next(s)}

    public activeCategory = "all";
    public selectionsLoaded: boolean = false;
    public currentSelection:ILibrarySelection = null;
    public nextSelection:ILibrarySelection = null;

    public searchTerm$ = new BehaviorSubject('');

    public categorySelected$: BehaviorSubject<Category | null> = new BehaviorSubject(null);

    public categories: Category[] = [
        {label: 'Arts', icon: 'arts.svg', contentAreas: ['Music', 'Performing Arts', 'Visual Arts']},
        {label: 'Science', icon: 'science.svg', contentAreas: ['Biology', 'Chemistry', 'Earth & Space Science', 'Physics', 'Technology & Engineering']},
        {label: 'Social Studies/History', icon: 'history.svg', contentAreas: ['Civics & Government', 'Economics', 'Social Studies', 'U.S. History', 'World History', 'World Languages']},
        {label: 'Language Arts', icon: 'language-arts.svg', contentAreas: ['English Language Arts']},
        {label: 'Math', icon: 'math.svg', contentAreas: ['Mathematics']},
        {label: 'Drama', icon: 'drama.svg', genres: ['Drama']},
        {label: 'Fantasy', icon: 'fantasy.svg', genres: ['Fantasy']},
        {label: 'Folklore/Fables/Myths', icon: 'folklore.svg', genres: ['Folklore/Fables/Myths']},
        {label: 'General Fiction', icon: 'general-fiction.svg', genres: ['General Fiction', 'Historical Fiction', 'Realistic Fiction']},
        {label: 'Nonfiction', icon: 'nonfiction.svg', genres: ['Biography/Autobiography', 'Narrative Nonfiction', 'Nonfiction']},
        {label: 'Mystery', icon: 'mistery.svg', genres: ['Mystery']},
        {label: 'Poetry', icon: 'poetry.svg', genres: ['Poetry']},
        {label: 'Science Fiction', icon: 'science-fiction.svg', genres: ['Science Fiction']},
    ];

    public browseBy: number = 0;

    public filteredSelections$ = combineLatest([this._selections, this.searchTerm$, this.categorySelected$]).pipe(
        debounceTime(300),
        map(([selections, searchTerm, categorySelected]) => {
            if (searchTerm.length === 0 && !categorySelected) return selections;
            let filteredSelections = selections;
            if (searchTerm.length > 0) {
                filteredSelections = selections.filter(selection => {
                    const _searchTerm = searchTerm.toLowerCase();
                    return  selection.storyDescription.fullTitle.toLowerCase().includes(_searchTerm) ||
                            selection.storyDescription?.contentTags?.map(tag => tag.name).join('|').toLowerCase().includes(_searchTerm) ||
                            selection.storyDescription?.contentAreas?.map(tag => tag.name).join('|').toLowerCase().includes(_searchTerm)
                })
            }
            if (!!categorySelected) {
                filteredSelections = this.getSelectionsByCategory(filteredSelections, categorySelected);
            }
            return filteredSelections;
        })
    );
    private hasIncompleteStory: boolean = true;

    @Input()
    public recognitionInitialized:boolean = false;

    constructor(private config: ClientConfiguration,
                private libraryService: LibraryService,
                private assignmentService: AssignmentService,
                private studentConfiguration: StudentConfiguration,
                private sessionService: SessionService,
                private eventBus: EventBus,
                private recognizerService: RecognizerService,
                private pipelineService: PipelineService,
                private ref: ChangeDetectorRef,
                private networkService: NetworkService,
                private modalConfig: ModalConfig,
                private audioQueueService: AudioQueueService,
                private log: BugsnagLoggingService,
                private gaEventService: GAEventService,
                private browserAgent: BrowserAgent
                ) {
        super("LibraryComponent");
        if (!this.assignmentService.canInitialize() && !(config.isSuperUserMode || config.isSuperUserDevMode)) {
            // if we can't get the assignments yet, we are loading components out of order (for unknown reasons)
            // wait for the library navigation event, at which point the user and session info will be loaded
            this.eventBus.pipe(filter((event: NavigationEvent) => event.type === NavigationEvent.TYPE
                    && event.location.length === 1 && event.location[0] === "library"))
                .subscribe((event) => {
                    this.modalConfig.openMessageModal("library_load_error");
                });
        } else {
            this.initiateLibrary();
        }
    }

    private initiateLibrary() {
        if (this.browserAgent.getBrowserName() === "Safari" && !this.sessionService.completedGesture) {
            // iPad requires a gesture for audio to play
            let gestureSubscribe = this.eventBus.pipe(filter(e => e instanceof GestureEvent)).subscribe((e: GestureEvent) => {
                if (e.status === GESTURE_STATUS.begin_gesture) {
                    this.initiateFetchSubscription();
                }
            });
            this.subscriptions.push(gestureSubscribe);
        } else {
            this.initiateFetchSubscription();
        }
    }

    private initiateFetchSubscription():void {
        let sub = this.sessionService.closeBookSession().subscribe(() => this.fetchLibrary());
        this.subscriptions.push(sub);
    }

    private initiateClickListening(): void {
        let fn = () => {
            this.assignmentService.setWelcomeMessagePlayed(true);
            document.removeEventListener("click", fn, false);
        };
        document.addEventListener("click", fn, false);
    }

    private playNextWelcomeAudio(): void {
        if (this.assignmentService.welcomeMessageNotPlayed()) {
            this.playHowToProceed();
        }
        this.assignmentService.setWelcomeMessagePlayed(true);
    }

    public playIntroductions(): void {
        // WELCOMING MESSAGING
        this.initiateClickListening();

        if (this.assignmentService.welcomeMessageNotPlayed()) {
            if ( this.assignmentService.isAssignmentNew() ) {
                this.audioQueueService.reset().subscribe(() => {
                    this.eventBus.next(new AudioQueueEvent([
                        new AudioQueueItem("RA_BEGIN_03", TranslateAudio.YES, () => {
                            this.playNextWelcomeAudio();
                        })
                    ], InterruptAudio.NO));
                });
            } else if ( !this.assignmentService.isAssignmentNew() ) {
                this.audioQueueService.reset().subscribe(() => {
                    this.eventBus.next(new AudioQueueEvent([
                        new AudioQueueItem("RA_BEGIN_02", TranslateAudio.YES, () => {
                            this.playNextWelcomeAudio();
                        })
                    ], InterruptAudio.NO));
                });
            }
        }
    }

    public playHowToProceed(): void {
        // AUDIO INSTRUCTIONS (HOW TO PROCEED)
        let currentSelectionNotStarted = this.assignmentService.currentSelectionStatus === CompletionState.NOT_STARTED;

        if (!this.assignmentService.welcomeMessageNotPlayed()) {
            return;
        } else if ( this.assignmentService.isLocked() && currentSelectionNotStarted) {
            // && Not Started
            this.audioQueueService.reset().subscribe(() => {
                this.eventBus.next(new AudioQueueEvent(
                    [   new AudioQueueItem("RA_LIBRARY_03", TranslateAudio.YES)
                    ], InterruptAudio.NO));
            });
        } else if ( (this.assignmentService.isUnlocked() || this.assignmentService.isSelectionUnlocked()) && currentSelectionNotStarted ) {
            // && Not Started
            this.audioQueueService.reset().subscribe(() => {
                this.eventBus.next(new AudioQueueEvent(
                    [   new AudioQueueItem("RA_LIBRARY_01", TranslateAudio.YES)
                    ], InterruptAudio.NO));
            });
        } else if ( this.assignmentService.isLocked() || this.assignmentService.isSelectionUnlocked() ) {
            // && Selection in Progress
            this.audioQueueService.reset().subscribe(() => {
                this.eventBus.next(new AudioQueueEvent(
                    [   new AudioQueueItem("RA_LIBRARY_02", TranslateAudio.YES)
                    ], InterruptAudio.NO));
            });
        }
    }

    public getSelectionsByCategory(selections: ILibrarySelection[], category: Category) {
        const selec = selections.filter(selection => {
            let hasAreas = false;
            let hasGenres = false;
            if (!!category.contentAreas && category?.contentAreas?.length > 0) {
                const contentAreas = new Set(category.contentAreas);
                hasAreas = selection.storyDescription?.contentAreas?.filter(({name}) => contentAreas.has(name)).length > 0;
            }
            if (!!category?.genres && category?.genres?.length > 0) {
                const genres = new Set(category.genres);
                hasGenres = genres.has(selection.storyDescription?.genre);
            }
            return hasAreas || hasGenres;
        });
        console.log(category.label, selec);
        return selec;
    }

    public fetchLibrary():void {
        this.eventBus.next(new EnableExitButtonEvent());
        this.assignmentService.retrieveAssignments()
            .subscribe((response:SelectionsAndCategories) => {
                this.selections = response.selections;
                this.categories = this.categories.map(category => {
                    return {...category, numOfItems: this.getSelectionsByCategory(this.selections, category).length}
                });
                this.hasIncompleteStory = response.selections.reduce((acc: boolean, selection) => {
                    return acc = acc ? true : selection.storyCompleteState === CompletionState.INCOMPLETE;
                }, false );

                let finishedSelections:ILibrarySelection[] = [];
                let unfinishedSelections:ILibrarySelection[] = [];

                this.selections.forEach((selection) => {
                    if (AssignmentService.isSelectionCompleted(selection)) {
                        finishedSelections.push(selection);
                    } else {
                        unfinishedSelections.push(selection);
                    }
                });

                this.currentSelection = this.libraryService
                    .determineCurrentSelection(this.selections, unfinishedSelections);

                this.nextSelection = this.libraryService
                    .determineNextSelection(this.currentSelection, unfinishedSelections);

                unfinishedSelections = unfinishedSelections.concat(finishedSelections);
                this.selections = unfinishedSelections;

                if (this.currentSelection) {
                    this.currentSelection.currentStep = this.assignmentService.getStartStep(this.currentSelection);
                    this.selections = this.selections.filter((selection) => selection !== this.currentSelection);
                }

                this.selectionsLoaded = true;

                this.recognizerService.init(this.studentConfiguration.pronunciationCorrectness).subscribe(() => {
                    this.eventBus.next(new GestureEvent(GESTURE_STATUS.end_gesture, ""));
                    this.recognitionInitialized = true;
                    this.ref.detectChanges();
                    this.eventBus.next(new PleaseWaitEvent({
                        show: false,
                        audio: false,
                        destination: PleaseWaitTarget.LIBRARY
                    }));
                    this.playIntroductions();
                    this.sessionService.setCompletedGesture(true);
                });
                this.gaEventService.endDurationEvent("app", "library-load", "complete");
                if (this.config.isSuperUserMode) {
                    this.eventBus.next(new PipelineEvent(PIPELINE_STATUS.manual));
                }
            });
    }

    public isSelectionAccessible(selection:ILibrarySelection):boolean {
        switch (this.studentConfiguration.contentProgression) {
            case ContentProgression.UNLOCKED:
                return true;
            case ContentProgression.STUDENT_SELECTED:
                return this.currentSelection === null
                       || selection === this.currentSelection
                       || AssignmentService.isSelectionCompleted(selection)
                       || selection.storyCompleteState === CompletionState.INCOMPLETE;
            case ContentProgression.LOCKED:
                return selection === this.currentSelection
                       || AssignmentService.isSelectionCompleted(selection)
                       || selection.storyCompleteState === CompletionState.INCOMPLETE;
            default:
                this.log.error("unknown progression: " + this.studentConfiguration.contentProgression, ErrorType.USER_STATE);
                return false;
        }
    }

    public isUserLocked() {
        return this.studentConfiguration.contentProgression === ContentProgression.LOCKED;
    }

    public isSearchAndBrowseByDisabled() {
        return this.studentConfiguration.contentProgression === ContentProgression.STUDENT_SELECTED && this.hasIncompleteStory;
   }

    public selectionClick(selection:ILibrarySelection):void {
      if (!this.networkService.isOnline()) {
            this.modalConfig.openMessageModal("exit_while_offline");
        } else if (this.config.isSuperUserMode) {
            this.eventBus.next(new PleaseWaitEvent({show: true, audio: true, destination: PleaseWaitTarget.READING}));
            this.pipelineService.retrieveUninflatedContent(selection.storyDescription.referenceStory)
                .subscribe(content => {
                    this.eventBus.next(new NavigationEvent(["reading", selection.storyDescription.referenceStory],
                        { queryParams: {isPipeline:true}}));
                });
        } else {
            if (this.isSelectionAccessible(selection)) {
                this.eventBus.next(new PleaseWaitEvent({show: true, audio: true, destination: PleaseWaitTarget.READING}));
                const gradeEquivForFormat = this.libraryService.getGradeEquivForFormat(selection, this.selections, this.currentSelection);
                this.assignmentService.startNewSelection(selection, gradeEquivForFormat);
            }
        }
        this.assignmentService.setWelcomeMessagePlayed(true);
    }

    public getLibraryImage(imgSrc:string):string {
        return this.config.libraryImageUrl(imgSrc);
    }

    public getSeriesTitle(selection:ILibrarySelection):string {
        return selection.storyDescription?.seriesName ? selection.storyDescription?.shortTitle : "";
    }

    public getBadges(selection:ILibrarySelection):string {
        let badge = "";
        // TODO: here, summary, and selection report all have this same calculation - consolidate to usermodel
        if (AssignmentService.isSelectionCompleted(selection)) {
            badge = <any>RecordingBadge.CHECKMARK;
            if (selection.hasQuizStar) {
                if (selection.recordingBadge === <any>RecordingBadge.GOLDEN) {
                    badge = <any>RecordingBadge.GOLDEN;
                }
                if (selection.recordingBadge === <any>RecordingBadge.SILVER) {
                    badge = <any>RecordingBadge.SILVER;
                }
            }
        }
        return badge;
    }

    public getCompleted(selection:ILibrarySelection):string {
        return this.isSelectionAccessible(selection) ? "" : "unaccessible";
    }

    public getIconUrl(icon: string) {
        const buildCode = !!+window.location.href.split('/')[3] && +window.location.href.split('/')[3] > 300 ? '/'+window.location.href.split('/')[3] : '';
        return `${buildCode}/img/categories/${icon}`;
    }

}
